import type { ExternalPathString } from "expo-router";
import { Link, router } from "expo-router";
import { useCallback, useRef } from "react";
import { Platform, View } from "react-native";
import { Display, mq } from "react-native-unistyles";

import Button from "$src/core/components/atoms/Button/Button";
import Image from "$src/core/components/atoms/Image/Image";
import ScrollView from "$src/core/components/atoms/ScrollView/ScrollView";
import Text from "$src/core/components/atoms/Text/Text";
import Footer from "$src/core/components/organisms/Footer/Footer.web";
import { TEST_IDS } from "$src/core/constants/test-ids";
import useAccessibilityAnnouncement from "$src/core/hooks/use-accessibility-announcement";
import useConfig from "$src/core/hooks/use-config";
import useLogo from "$src/core/hooks/use-logo";
import useTranslation from "$src/core/hooks/use-translation";
import useTvEntryFocus from "$src/core/hooks/use-tv-entry-focus";

import $ from "./IntroPage.styles";

const FooterLink = ({ text, href }: { text: string; href: string }) => (
  <View style={$.footerLinks}>
    <Link href={href as ExternalPathString} target="_blank">
      <Text accessibilityRole="text">{text}</Text>
    </Link>
  </View>
);

const IntroPage: React.FC = () => {
  const { name: appName, linking } = useConfig();
  const { t } = useTranslation();
  const logo = useLogo();
  const signInButtonRef = useRef<View>(null);
  const onActionsLayout = useTvEntryFocus(signInButtonRef);

  const handleLogin = () => {
    router.navigate("/login");
  };

  const handleSkip = () => {
    if (router.canDismiss()) {
      router.dismissAll();
    }
    router.replace("/storefront");
  };

  useAccessibilityAnnouncement(
    useCallback(
      ({ announce, focusAfter }) => {
        announce(
          t("INTRO/WELCOME_ANNOUNCEMENT", {
            appName,
            title: t("INTRO/TITLE"),
            subtitle: t("INTRO/SUBTITLE", { appName }),
          }),
        );
        focusAfter(signInButtonRef);
      },
      [appName, t],
    ),
    { enabled: Platform.isTV },
  );

  return (
    <>
      <ScrollView contentContainerStyle={$.container} accessibilityRole="list">
        <View>
          {/* This is a decorative image, so we don't need it to be accessible */}
          {/* eslint-disable-next-line triple-rn-a11y/accessibility-label */}
          <Image
            source={logo}
            contentFit="contain"
            style={$.logo}
            accessibilityRole="none"
            importantForAccessibility="no"
          />

          <Text preset="heading2" accessibilityRole="header" style={$.title}>
            {t("INTRO/TITLE")}
          </Text>
          <Text
            preset="body"
            accessibilityRole={Platform.OS === "web" ? "text" : "summary"}
            style={$.body}
          >
            {t("INTRO/SUBTITLE", { appName })}
          </Text>

          <View style={$.actions} onLayout={onActionsLayout}>
            <Button
              ref={signInButtonRef}
              name="sign_in"
              preset="primary"
              onPress={handleLogin}
              icon="sign-in"
              label={t("INTRO/PAGE/SIGN_IN/CTA/LABEL")}
              accessibilityHint={t("INTRO/SIGN_IN/CTA/HINT")}
              focusable
              testID={TEST_IDS.intro.signInButton}
            />
            <Button
              name="skip_sign_in"
              preset="secondary"
              onPress={handleSkip}
              label={t("INTRO/SKIP")}
              icon="arrow-right"
              accessibilityHint={t("INTRO/SKIP/CTA/HINT")}
              testID={TEST_IDS.intro.skipButton}
            />
          </View>
        </View>
      </ScrollView>

      <Display mq={mq.only.width("lg", undefined)}>
        <View style={$.footer}>
          <Footer />
          <View style={$.footerLinks}>
            <FooterLink
              text={t("FOOTER/PRIVACY_POLICY")}
              href={linking.footer.privacyPolicy}
            />
            <FooterLink
              text={t("FOOTER/TERMS_OF_SERVICE")}
              href={linking.footer.termsOfService}
            />
            <FooterLink
              text={t("FOOTER/COOKIES_POLICY")}
              href={linking.footer.cookiesPolicy}
            />
            <FooterLink text={t("FOOTER/HELP")} href={linking.footer.help} />
            <FooterLink text={t("FOOTER/ABOUT")} href={linking.footer.about} />
          </View>
        </View>
      </Display>
    </>
  );
};

export default IntroPage;
